// ── itemPrices.js — 품목 가격 시계열 헬퍼 ───────────────────────────────────
// 영수증 OCR의 lineItems → users/{uid}/itemPrices Firestore collection으로 누적.
// 단가표(prices)와 fuzzy match해서 normalizedName 부여.
// 사장이 단가표에 등록한 이름이 정규화 기준 — 같은 양파를 "양파 1망", "유기농 양파"
// 등으로 사도 다 단가표의 "양파"로 묶임.

import { collection, addDoc, query, where, orderBy, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../db';

// ── 정규화 헬퍼 ────────────────────────────────────────────────────────────
function normalize(text) {
  return String(text || '')
    .replace(/\s+/g, '')
    .replace(/[()[\]{}<>]/g, '')
    .toLowerCase();
}

// ── 매칭 — 단가표의 name과 가장 가까운 항목 찾기 ──────────────────────────
/**
 * @param {string} rawName  OCR이 추출한 품목명 (예: "유기농 양파 1망")
 * @param {Array} prices    단가표 [{ code, name, spec, ... }]
 * @returns {{ matched: object|null, score: number, method: string }}
 */
export function matchPriceItem(rawName, prices) {
  if (!rawName || !Array.isArray(prices) || prices.length === 0) {
    return { matched: null, score: 0, method: 'no_input' };
  }
  const raw = normalize(rawName);
  if (!raw) return { matched: null, score: 0, method: 'empty' };

  // 1차: 정확 일치
  for (const p of prices) {
    if (normalize(p.name) === raw) {
      return { matched: p, score: 1.0, method: 'exact' };
    }
  }

  // 2차: 단가표 이름이 raw에 포함되어 있음 (예: 단가표="양파", raw="유기농양파1망")
  let bestContains = null;
  let bestContainsLen = 0;
  for (const p of prices) {
    const pn = normalize(p.name);
    if (pn.length >= 2 && raw.includes(pn) && pn.length > bestContainsLen) {
      bestContains = p;
      bestContainsLen = pn.length;
    }
  }
  if (bestContains) {
    // 단가표 이름이 길수록 신뢰도 ↑ (예: "다진마늘" 매칭이 "마늘"보다 신뢰)
    const score = Math.min(0.95, 0.7 + bestContainsLen * 0.05);
    return { matched: bestContains, score, method: 'contains' };
  }

  // 3차: raw가 단가표 이름에 포함되어 있음 (예: 단가표="양파 1.5kg", raw="양파")
  let bestReverse = null;
  for (const p of prices) {
    const pn = normalize(p.name);
    if (raw.length >= 2 && pn.includes(raw)) {
      bestReverse = p;
      break;
    }
  }
  if (bestReverse) {
    return { matched: bestReverse, score: 0.7, method: 'reverse_contains' };
  }

  // 4차: 첫 글자 같고 길이 비슷한 것 (간단한 fuzzy)
  for (const p of prices) {
    const pn = normalize(p.name);
    if (pn.length >= 2 && raw.length >= 2 && pn[0] === raw[0]) {
      const lenDiff = Math.abs(pn.length - raw.length);
      if (lenDiff <= 2) {
        // 약한 매칭 — 사용자 확인 필요
        return { matched: p, score: 0.5, method: 'first_char' };
      }
    }
  }

  return { matched: null, score: 0, method: 'unmatched' };
}

// ── 저장 — lineItems를 itemPrices로 풀어서 누적 ──────────────────────────
/**
 * 거래 1건의 lineItems를 itemPrices 컬렉션에 push.
 * @param {string} uid
 * @param {Array} lineItems    [{ itemName, amount, ... }]
 * @param {Array} prices       단가표
 * @param {object} ctx         { date, partnerName, txnId, source }
 * @returns {Promise<{saved: number, unmatched: Array}>}
 */
export async function saveItemPrices(uid, lineItems, prices, ctx = {}) {
  if (!uid || !Array.isArray(lineItems) || lineItems.length === 0) {
    return { saved: 0, unmatched: [] };
  }

  const ref = collection(db, 'users', uid, 'itemPrices');
  const unmatched = [];
  let saved = 0;

  for (const li of lineItems) {
    const rawName = String(li.itemName || '').trim();
    const amount = Number(li.amount) || 0;
    if (!rawName || amount <= 0) continue;  // 음수(할인 라인)·이름 없는 건 스킵

    const m = matchPriceItem(rawName, prices);
    const doc = {
      rawName,
      normalizedName: m.matched ? m.matched.name : null,
      priceCode: m.matched ? m.matched.code : null,
      matchScore: m.score,
      matchMethod: m.method,
      amount,
      date: ctx.date || new Date().toISOString().slice(0, 10),
      partnerName: ctx.partnerName || null,
      txnId: ctx.txnId || null,
      source: ctx.source || 'ocr',
      createdAt: serverTimestamp(),
    };

    try {
      await addDoc(ref, doc);
      saved++;
      if (!m.matched || m.score < 0.7) {
        unmatched.push({ rawName, score: m.score, method: m.method });
      }
    } catch (e) {
      console.error('itemPrices save failed:', e);
    }
  }

  return { saved, unmatched };
}

// ── 집계 — 품목별 평균가 ──────────────────────────────────────────────────
/**
 * 특정 품목의 기간별 평균가 조회
 * @param {string} uid
 * @param {string} normalizedName  단가표 정규화 이름 (예: "양파")
 * @param {object} opts            { fromDate?, toDate?, limit? }
 */
export async function fetchItemPriceHistory(uid, normalizedName, opts = {}) {
  if (!uid || !normalizedName) return [];
  const { fromDate, toDate } = opts;
  const ref = collection(db, 'users', uid, 'itemPrices');
  const constraints = [where('normalizedName', '==', normalizedName)];
  if (fromDate) constraints.push(where('date', '>=', fromDate));
  if (toDate) constraints.push(where('date', '<=', toDate));
  constraints.push(orderBy('date', 'desc'));

  try {
    const q = query(ref, ...constraints);
    const snap = await getDocs(q);
    const list = [];
    snap.forEach(d => list.push({ id: d.id, ...d.data() }));
    return list;
  } catch (e) {
    console.error('itemPrice history fetch failed:', e);
    return [];
  }
}

/**
 * 단가표 전체에 대해 지난 N일 평균가 계산
 * @param {string} uid
 * @param {Array} prices  단가표
 * @param {number} days   기본 30일
 * @returns {Promise<Map<string, { avg, count, min, max, lastDate }>>}
 *          key = price.code, value = stats
 */
export async function computePriceAverages(uid, prices, days = 30) {
  if (!uid || !Array.isArray(prices)) return new Map();

  const fromDate = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
  const ref = collection(db, 'users', uid, 'itemPrices');
  const q = query(ref, where('date', '>=', fromDate), orderBy('date', 'desc'));

  try {
    const snap = await getDocs(q);
    const map = new Map();  // priceCode → { sum, count, min, max, lastDate, samples[] }
    snap.forEach(d => {
      const data = d.data();
      const code = data.priceCode;
      if (!code) return;
      let entry = map.get(code);
      if (!entry) {
        entry = { sum: 0, count: 0, min: Infinity, max: -Infinity, lastDate: null, samples: [] };
        map.set(code, entry);
      }
      const a = Number(data.amount) || 0;
      if (a <= 0) return;
      entry.sum += a;
      entry.count++;
      if (a < entry.min) entry.min = a;
      if (a > entry.max) entry.max = a;
      if (!entry.lastDate || data.date > entry.lastDate) entry.lastDate = data.date;
      entry.samples.push({ date: data.date, amount: a });
    });

    // sum → avg
    const result = new Map();
    for (const [code, e] of map.entries()) {
      result.set(code, {
        avg: e.count > 0 ? Math.round(e.sum / e.count) : 0,
        count: e.count,
        min: e.min === Infinity ? 0 : e.min,
        max: e.max === -Infinity ? 0 : e.max,
        lastDate: e.lastDate,
        samples: e.samples,
      });
    }
    return result;
  } catch (e) {
    console.error('computePriceAverages failed:', e);
    return new Map();
  }
}
